import { Chip } from '@/components/ui/chip';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/classUtils';
import { Check, ChevronDown, ChevronLeft, X } from 'lucide-react';
import * as React from 'react';
import { type DateRange as DayPickerDateRange } from 'react-day-picker';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface DateRange {
  from: string | undefined;
  to: string | undefined;
}

interface DateRangeSelectProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  placeholder?: string;
  className?: string;
  label?: string;
  /** When true, renders only the content without a popover wrapper */
  embedded?: boolean;
  /** When true, opens directly in calendar mode instead of showing presets */
  calendarOnly?: boolean;
}

const formatDateShort = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const formatToString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const getDateYearsAgo = (years: number): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - years);
  return date;
};

const PRESETS = [
  { years: 1, label: 'Last 1 year' },
  { years: 5, label: 'Last 5 years' },
  { years: 10, label: 'Last 10 years' },
] as const;

type ViewMode = 'presets' | 'calendar';

// Check if the current value matches a preset
const getMatchingPreset = (value: DateRange): number | null => {
  if (!value.from || !value.to) return null;

  const today = new Date();
  const todayStr = formatToString(today);

  // Check if "to" date is today
  if (value.to !== todayStr) return null;

  // Check each preset
  for (const preset of PRESETS) {
    const fromDate = getDateYearsAgo(preset.years);
    const fromStr = formatToString(fromDate);
    if (value.from === fromStr) {
      return preset.years;
    }
  }

  return null;
};

export const DateRangeSelect: React.FC<DateRangeSelectProps> = ({
  value,
  onChange,
  placeholder = 'Date',
  className,
  label = 'Date',
  embedded = false,
  calendarOnly = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<ViewMode>(
    calendarOnly ? 'calendar' : 'presets'
  );

  const hasValue = value.from || value.to;
  const matchingPreset = getMatchingPreset(value);
  const isCustomRange = hasValue && matchingPreset === null;

  // Determine initial view mode when popover opens
  React.useEffect(() => {
    if (open) {
      // If calendarOnly or current value is a custom range, open directly in calendar mode
      setViewMode(calendarOnly || isCustomRange ? 'calendar' : 'presets');
    }
  }, [open, isCustomRange, calendarOnly]);

  const fromDate = value.from ? new Date(value.from + 'T00:00:00') : undefined;
  const toDate = value.to ? new Date(value.to + 'T00:00:00') : undefined;

  const displayValue = React.useMemo(() => {
    if (value.from && value.to) {
      return `${formatDateShort(value.from)} ... ${formatDateShort(value.to)}`;
    }
    if (value.from) {
      return `${formatDateShort(value.from)} ...`;
    }
    if (value.to) {
      return `... ${formatDateShort(value.to)}`;
    }
    return '';
  }, [value.from, value.to]);

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onChange({ from: undefined, to: undefined });
    setOpen(false);
  };

  const handlePresetSelect = (years: number) => {
    const today = new Date();
    const fromDate = getDateYearsAgo(years);
    onChange({
      from: formatToString(fromDate),
      to: formatToString(today),
    });
    setOpen(false);
  };

  const handleCalendarSelect = (range: DayPickerDateRange | undefined) => {
    onChange({
      from: range?.from ? formatToString(range.from) : undefined,
      to: range?.to ? formatToString(range.to) : undefined,
    });
  };

  const selectedRange: DayPickerDateRange | undefined =
    fromDate || toDate ? { from: fromDate, to: toDate } : undefined;

  const trigger = hasValue ? (
    <Chip
      className={cn(
        '!bg-yellow-50 hover:!bg-yellow-100 focus-visible:!bg-yellow-50 focus-visible:!border-neutral-300 focus-visible:outline-none active:!bg-yellow-100',
        className
      )}
    >
      <Chip.Label>
        <FlexRow gap="1.5" alignItems="center" className="min-w-0">
          <Text
            variant="body-sm"
            className="font-medium text-neutral-900 min-w-0 whitespace-nowrap"
          >
            <span className="truncate-label">{label}</span>:{' '}
            <span className="font-bold">{displayValue}</span>
          </Text>
          <button
            type="button"
            onClick={handleClear}
            onMouseDown={e => e.stopPropagation()}
            className="shrink-0 flex items-center justify-center hover:bg-yellow-100 rounded p-0.5"
          >
            <X size={16} className="text-neutral-600" />
          </button>
        </FlexRow>
      </Chip.Label>
    </Chip>
  ) : (
    <Chip className={className}>
      <Chip.Label>
        <FlexRow gap="1.5" alignItems="center" className="whitespace-nowrap">
          <Text
            variant="body-sm"
            className="font-medium text-neutral-900 truncate-label"
          >
            {placeholder}
          </Text>
          <ChevronDown size={16} className="text-neutral-600" />
        </FlexRow>
      </Chip.Label>
    </Chip>
  );

  const content =
    viewMode === 'presets' ? (
      <FlexCol className="min-w-[200px] py-1">
        {PRESETS.map(preset => {
          const isSelected = matchingPreset === preset.years;
          return (
            <button
              key={preset.years}
              type="button"
              onClick={() => handlePresetSelect(preset.years)}
              className="flex items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent transition-colors"
            >
              <span>{preset.label}</span>
              {isSelected ? (
                <Check size={16} className="shrink-0 text-primary" />
              ) : (
                <span className="h-4 w-4 shrink-0" />
              )}
            </button>
          );
        })}
        <div className="my-1 border-t" />
        <button
          type="button"
          onClick={() => setViewMode('calendar')}
          className="flex items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent transition-colors"
        >
          <span>Custom range</span>
          {isCustomRange ? (
            <Check size={16} className="shrink-0 text-primary" />
          ) : (
            <span className="h-4 w-4 shrink-0" />
          )}
        </button>
      </FlexCol>
    ) : (
      <FlexCol>
        {!calendarOnly && (
          <button
            type="button"
            onClick={() => setViewMode('presets')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors border-b"
          >
            <ChevronLeft size={16} />
            Back
          </button>
        )}
        <Calendar
          mode="range"
          selected={selectedRange}
          onSelect={handleCalendarSelect}
          initialFocus
        />
      </FlexCol>
    );

  // In embedded mode, just return the content without popover wrapper
  if (embedded) {
    return content;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {content}
      </PopoverContent>
    </Popover>
  );
};
